import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, ChatCircle, Check, Circle, Clock, FileText, FolderSimple,
  PaperPlaneTilt, SignOut, UploadSimple, Warning, Paperclip, X, Files,
} from "@phosphor-icons/react";
import { ReadReceipt } from "@/components/app/ReadReceipt";
import { MessageAttachmentView, MessageGalleryProvider } from "@/components/app/MessageAttachmentView";
import { usePresenceWebSocket } from "@/hooks/usePresenceWebSocket";

const formatBytes = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${(b / 1024).toFixed(0)} KB`);

export default function ReferrerCaseDetailPage() {
  const { token, caseId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "messages" ? "messages"
    : searchParams.get("tab") === "documents" ? "documents"
    : "progress";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [sendingNote, setSendingNote] = useState(false);
  const [noteFiles, setNoteFiles] = useState([]);
  const [uploadingNoteFile, setUploadingNoteFile] = useState(false);
  const noteFileInputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const dragDepthRef = useRef(0);
  const [docPickerOpen, setDocPickerOpen] = useState(false);
  const [docPickerSearch, setDocPickerSearch] = useState("");
  const [activeTab, setActiveTab] = useState(initialTab);
  const messagesEndRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const { data: dash } = await API.get(`/referrer-dashboard/${token}`);
      setData(dash);
      // Mark all notes on this case as seen by this referrer (silent, non-blocking)
      API.post(`/cases/${caseId}/notes-seen`, null, { headers: { "x-referrer-token": token } }).catch(() => {});
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired link");
    } finally {
      setLoading(false);
    }
  }, [token, caseId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Live updates
  const { peers, typingActors, sendTyping } = usePresenceWebSocket(
    token && caseId ? `/api/ws/referrer/${token}/${caseId}` : null,
    useCallback((msg) => {
      if (msg.type === "referrer_note_created" && msg.data?.case_id === caseId) {
        setData((prev) => prev ? { ...prev, notes: [msg.data, ...(prev.notes || []).filter((n) => n.id !== msg.data.id)] } : prev);
      } else if (["note_created", "stage_updated", "stages_updated", "document_uploaded", "referrer_document_uploaded"].includes(msg.type)) {
        fetchData();
      }
    }, [caseId, fetchData]),
  );

  useEffect(() => {
    if (activeTab === "messages" && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [activeTab, data?.notes?.length]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-slate-300 border-t-[#100f5d] rounded-full animate-spin" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-6">
        <Warning weight="fill" className="w-12 h-12 text-slate-300 mb-3" />
        <p className="text-sm text-slate-500 font-medium">{error || "Data not found"}</p>
        <Link to={`/referrer-dashboard/${token}`} className="mt-4 text-xs underline" style={{ color: "#100f5d" }}>Back to dashboard</Link>
      </div>
    );
  }

  const caseData = (data.cases || []).find((c) => c.id === caseId);
  if (!caseData) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-6">
        <Warning weight="fill" className="w-12 h-12 text-slate-300 mb-3" />
        <p className="text-sm text-slate-500 font-medium">Case not found in your referrals</p>
        <Link to={`/referrer-dashboard/${token}`} className="mt-4 text-xs underline" style={{ color: "#100f5d" }}>Back to dashboard</Link>
      </div>
    );
  }

  const referrer = data.referrer || {};
  const caseNotes = (data.notes || []).filter((n) => n.case_id === caseId);
  const caseDocs = (data.documents || []).filter((d) => d.case_id === caseId);
  const stages = caseData.stages || [];

  const handleSendNote = async () => {
    if (!noteText.trim() && noteFiles.length === 0) return;
    setSendingNote(true);
    try {
      await API.post(`/referrer-notes`, {
        content: noteText.trim(),
        case_id: caseId,
        attachment_ids: noteFiles.map((a) => a.id),
      }, { headers: { "x-referrer-token": token } });
      setNoteText("");
      setNoteFiles([]);
      sendTyping(false);
      await fetchData();
    } catch {
      toast.error("Failed to send message");
    } finally {
      setSendingNote(false);
    }
  };

  const uploadNoteAttachment = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setUploadingNoteFile(true);
    for (const file of list) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data: att } = await API.post(`/cases/${caseId}/message-attachments?channel=referrer`, fd,
          { headers: { "x-referrer-token": token, "Content-Type": "multipart/form-data" } });
        if ((file.type || "").startsWith("image/")) att.preview = URL.createObjectURL(file);
        setNoteFiles((p) => [...p, att]);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Upload failed: ${file.name}`);
      }
    }
    setUploadingNoteFile(false);
  };

  const removePendingAttachment = async (attId) => {
    setNoteFiles((p) => {
      const found = p.find((a) => a.id === attId);
      if (found?.preview) URL.revokeObjectURL(found.preview);
      return p.filter((a) => a.id !== attId);
    });
    try { await API.delete(`/message-attachments/${attId}`, { headers: { "x-referrer-token": token } }); } catch { /* best-effort */ }
  };

  const attachFromReferrerDoc = async (d) => {
    try {
      const { data: att } = await API.post(`/cases/${caseId}/message-attachments/from-document`, {
        document_id: d.id, channel: "referrer", source: "referrer_doc",
      }, { headers: { "x-referrer-token": token } });
      setNoteFiles((p) => [...p, att]);
      toast.success(`${d.original_filename} attached`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to attach document");
    }
  };

  const loadBlob = async (attId) => {
    const resp = await API.get(`/message-attachments/${attId}/download`, {
      responseType: "blob", headers: { "x-referrer-token": token },
    });
    return resp.data;
  };

  const collectImages = (noteList) =>
    noteList.flatMap((n) => (n.attachments || []).filter((a) => (a.content_type || "").startsWith("image/")));

  const handlePaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const files = items.filter((it) => it.kind === "file" && it.type.startsWith("image/")).map((it) => it.getAsFile()).filter(Boolean);
    if (files.length) {
      e.preventDefault();
      uploadNoteAttachment(files);
    }
  };

  const deleteAttachment = async (attId) => {
    try {
      await API.delete(`/message-attachments/${attId}`, { headers: { "x-referrer-token": token } });
      toast.success("Attachment deleted");
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete attachment");
    }
  };

  const chatDrag = {
    onDragEnter: (e) => {
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault(); e.stopPropagation();
      dragDepthRef.current += 1; setDragging(true);
    },
    onDragOver: (e) => {
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault(); e.stopPropagation();
    },
    onDragLeave: (e) => {
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault(); e.stopPropagation();
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) setDragging(false);
    },
    onDrop: (e) => {
      e.preventDefault(); e.stopPropagation();
      dragDepthRef.current = 0; setDragging(false);
      if (e.dataTransfer?.files?.length) uploadNoteAttachment(e.dataTransfer.files);
    },
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await API.post(`/referrer-documents/${caseId}`, fd, { headers: { "x-referrer-token": token, "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      await fetchData();
    } catch {
      toast.error("Upload failed");
    }
    e.target.value = "";
  };

  const tabs = [
    { key: "progress", label: "Progress", icon: FolderSimple, count: stages.length },
    { key: "messages", label: "Messages", icon: ChatCircle, count: caseNotes.length },
    { key: "documents", label: "Documents", icon: FileText, count: caseDocs.length },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="referrer-case-detail-page">
      {/* Header */}
      <div style={{ background: "#100f5d" }}>
        <div className="h-[3px]" style={{ background: "#c87d3a" }} />
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate(`/referrer-dashboard/${token}`)}
              className="w-9 h-9 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity shrink-0"
              style={{ background: "rgba(255,255,255,0.12)" }}
              data-testid="back-to-dashboard-btn">
              <ArrowLeft weight="bold" className="w-4 h-4 text-white" />
            </button>
            <img src="/logo.png" alt="UKC Legal" className="h-9 w-auto hidden sm:block" />
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-white truncate" style={{ fontFamily: "Outfit, sans-serif" }}>{caseData.title}</h1>
              <p className="text-xs text-slate-300 truncate">{caseData.property_address} · {caseData.client_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="text-right">
              <p className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>{caseData.progress}%</p>
              <p className="text-xs" style={{ color: "#e89e63" }}>complete</p>
            </div>
            {/* Sign-out — same behaviour as the dashboard button: revoke the
                JWT (if any) and bounce to /referrer-login. Icon-only on mobile. */}
            <button
              onClick={async () => {
                try {
                  if (user) await logout();
                } finally {
                  try {
                    localStorage.removeItem("access_token");
                    localStorage.removeItem("refresh_token");
                  } catch { /* noop */ }
                  navigate("/referrer-login");
                }
              }}
              data-testid="referrer-logout-btn"
              title="Sign out"
              className="inline-flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-xs font-medium text-white/80 rounded-lg transition-colors hover:bg-white/10 hover:text-white"
            >
              <SignOut weight="bold" className="w-4 h-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* Tab strip */}
        <div className="flex gap-1 mb-6 bg-white border border-slate-200 rounded-lg p-1 w-fit">
          {tabs.map((tab) => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} data-testid={`detail-tab-${tab.key}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-medium transition-colors ${
                activeTab === tab.key ? "text-white" : "text-slate-500 hover:text-slate-700"
              }`}
              style={activeTab === tab.key ? { background: "#100f5d" } : {}}>
              <tab.icon weight={activeTab === tab.key ? "fill" : "regular"} className="w-3.5 h-3.5" />
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Progress Tab */}
        {activeTab === "progress" && (
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 space-y-3" data-testid="progress-section">
            {stages.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">No progress information yet.</p>
            ) : stages.map((s) => {
              const subs = s.subcategories || [];
              const subsC = subs.filter((x) => x.completed).length;
              return (
                <div key={s.key} className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                      s.status === "completed" ? "bg-emerald-600" : s.status === "current" ? "" : "bg-slate-200"
                    }`} style={s.status === "current" ? { background: "#100f5d" } : {}}>
                      {s.status === "completed" ? <Check weight="bold" className="w-3 h-3 text-white" /> :
                       s.status === "current" ? <Clock weight="bold" className="w-3 h-3 text-white" /> :
                       <Circle weight="bold" className="w-3 h-3 text-slate-400" />}
                    </div>
                    <span className={`text-sm font-medium flex-1 ${s.status === "completed" ? "text-emerald-700" : s.status === "current" ? "text-slate-900" : "text-slate-400"}`}>
                      {s.label}
                    </span>
                    {subs.length > 0 && <span className="text-[11px] text-slate-400">{subsC}/{subs.length}</span>}
                    {s.completed_at && <span className="text-[11px] text-slate-400">{new Date(s.completed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>}
                  </div>
                  {subs.length > 0 && (
                    <ul className="ml-8 space-y-1">
                      {subs.map((sub) => (
                        <li key={sub.id} className="flex items-center gap-2 text-xs">
                          <div className={`w-3.5 h-3.5 rounded-sm flex items-center justify-center shrink-0 border ${
                            sub.completed ? "bg-emerald-500 border-emerald-500" : "bg-white border-slate-300"
                          }`}>
                            {sub.completed && <Check weight="bold" className="w-2.5 h-2.5 text-white" />}
                          </div>
                          <span className={sub.completed ? "text-slate-500 line-through" : "text-slate-700"}>{sub.label}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Messages Tab — chat window scoped to this case */}
        {activeTab === "messages" && (
          <div className="relative bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid="messages-section" {...chatDrag}>
            {dragging && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-50/90 border-2 border-dashed rounded-lg pointer-events-none" style={{ borderColor: "#100f5d" }} data-testid="messages-drop-overlay">
                <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "#100f5d" }}>
                  <UploadSimple weight="bold" className="w-5 h-5" /> Drop to attach
                </div>
              </div>
            )}
            <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2" style={{ background: "rgba(16,15,93,0.04)" }}>
              <ChatCircle weight="fill" className="w-4 h-4" style={{ color: "#100f5d" }} />
              <span className="text-sm font-semibold" style={{ color: "#100f5d", fontFamily: "Outfit, sans-serif" }}>
                Messages on {caseData.title}
              </span>
              <span className="text-[11px] text-slate-400 ml-auto">{caseNotes.length} message{caseNotes.length === 1 ? "" : "s"}</span>
            </div>

            <div className="p-5 max-h-[460px] overflow-y-auto bg-slate-50/40">
              {caseNotes.length === 0 ? (
                <div className="text-center py-10">
                  <ChatCircle weight="thin" className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-xs text-slate-400">No messages yet — send the first message below.</p>
                </div>
              ) : (
                <MessageGalleryProvider images={collectImages([...caseNotes].reverse())} loadBlob={loadBlob}>
                <div className="space-y-3">
                  {[...caseNotes].reverse().map((n) => {
                    const isMine = n.author_role === "referrer" && n.author_name === referrer.name;
                    return (
                      <div key={n.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`} data-testid={`note-${n.id}`}>
                        <div className={`max-w-[78%] px-3.5 py-2.5 rounded-lg text-sm shadow-sm ${isMine ? "text-white" : "bg-white text-slate-900 border border-slate-200"}`}
                          style={isMine ? { background: "#100f5d" } : {}}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-[10px] font-semibold ${isMine ? "text-slate-200" : "text-slate-700"}`}>{n.author_name}</span>
                            <span className={`text-[9px] uppercase tracking-wide ${isMine ? "text-slate-300" : "text-slate-400"}`}>{n.author_role}</span>
                          </div>
                          <p className="whitespace-pre-wrap leading-snug">{n.content}</p>
                          {(n.attachments || []).length > 0 && (
                            <div className="flex flex-col gap-1.5 mt-1.5 items-start" data-testid={`note-attachments-${n.id}`}>
                              {n.attachments.map((a) => (
                                <MessageAttachmentView
                                  key={a.id}
                                  attachment={a}
                                  loadBlob={loadBlob}
                                  onDelete={deleteAttachment}
                                  canDelete={isMine}
                                  testidPrefix="note-attachment"
                                />
                              ))}
                            </div>
                          )}
                          <div className={`text-[10px] mt-1 ${isMine ? "text-slate-300" : "text-slate-400"}`}>
                            {new Date(n.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                          </div>
                          {isMine && (
                            <div className="mt-1"><ReadReceipt seenBy={n.seen_by} currentActorName={referrer.name} currentActorRole="referrer" /></div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
                </MessageGalleryProvider>
              )}
            </div>

            {(peers.length > 0 || typingActors.length > 0) && (
              <div className="px-5 pt-2 pb-1 text-[11px] text-slate-500 border-t border-slate-100">
                {typingActors.length > 0 ? `${typingActors[0].name} is typing…` : `${peers.length} online`}
              </div>
            )}

            <div className="border-t border-slate-100 p-4 space-y-2">
              <p className="text-[11px] text-slate-500 leading-snug">
                <Clock weight="bold" className="w-3 h-3 inline-block mr-1 -mt-0.5 text-slate-400" />
                Whilst this is an instant message, replies follow standard response times. We aim to review messages within <span className="font-medium text-slate-600">48 hours</span> — at busy times this may be longer.
              </p>
              {noteFiles.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="note-pending-attachments">
                  {noteFiles.map((a) => (
                    <span key={a.id} className="inline-flex items-center gap-1 text-[11px] bg-slate-100 border border-slate-200 rounded-full pl-1 pr-1 py-0.5 text-slate-700" data-testid={`note-pending-${a.id}`}>
                      {a.preview
                        ? <img src={a.preview} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" data-testid={`note-pending-thumb-${a.id}`} />
                        : <Paperclip weight="bold" className="w-2.5 h-2.5 ml-1" />}
                      <span className="max-w-[140px] truncate">{a.original_filename}</span>
                      <button type="button" onClick={() => removePendingAttachment(a.id)} className="hover:text-rose-600" data-testid={`note-pending-remove-${a.id}`}>
                        <X weight="bold" className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input ref={noteFileInputRef} type="file" multiple className="hidden" data-testid="note-file-input"
                  onChange={(e) => { uploadNoteAttachment(e.target.files); e.target.value = ""; }} />
                <Button type="button" variant="outline" onClick={() => noteFileInputRef.current?.click()} disabled={uploadingNoteFile}
                  data-testid="note-attach-btn" title="Upload a file from your computer"
                  className="h-10 w-10 p-0 shrink-0 border-slate-200 text-slate-500 hover:text-slate-900">
                  {uploadingNoteFile
                    ? <span className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                    : <Paperclip weight="bold" className="w-4 h-4" />}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setDocPickerSearch(""); setDocPickerOpen(true); }}
                  data-testid="note-attach-doc-btn" title="Attach a file already on this case"
                  className="h-10 w-10 p-0 shrink-0 border-slate-200 text-slate-500 hover:text-slate-900">
                  <Files weight="bold" className="w-4 h-4" />
                </Button>
                <Input
                  data-testid="note-input"
                  placeholder="Message the solicitor about this case…"
                  value={noteText}
                  onChange={(e) => {
                    setNoteText(e.target.value);
                    if (e.target.value.trim()) sendTyping(true); else sendTyping(false);
                  }}
                  onBlur={() => sendTyping(false)}
                  onPaste={handlePaste}
                  onKeyDown={(e) => e.key === "Enter" && (noteText.trim() || noteFiles.length) && handleSendNote()}
                  className="h-10 flex-1" />
                <Button onClick={handleSendNote} disabled={sendingNote || (!noteText.trim() && noteFiles.length === 0)}
                  data-testid="send-note-btn"
                  className="h-10 px-4 text-white hover:opacity-90"
                  style={{ background: "#100f5d" }}>
                  <PaperPlaneTilt weight="bold" className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === "documents" && (
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm" data-testid="documents-section">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText weight="fill" className="w-4 h-4" style={{ color: "#100f5d" }} />
                <span className="text-sm font-semibold" style={{ color: "#100f5d", fontFamily: "Outfit, sans-serif" }}>
                  Case Documents
                </span>
                <span className="text-[11px] text-slate-400">· {caseDocs.length}</span>
              </div>
              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-md cursor-pointer hover:opacity-90 transition-opacity"
                style={{ background: "#100f5d" }} data-testid="upload-doc-btn">
                <UploadSimple weight="bold" className="w-3.5 h-3.5" /> Upload
                <input type="file" className="hidden" onChange={handleUpload} />
              </label>
            </div>
            <div className="divide-y divide-slate-100">
              {caseDocs.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-10">No documents uploaded yet.</p>
              ) : caseDocs.map((d) => (
                <div key={d.id} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50" data-testid={`doc-${d.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText weight="regular" className="w-4 h-4 text-slate-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-slate-900 truncate">{d.original_filename || d.name || "Document"}</p>
                      <p className="text-[11px] text-slate-400">{formatBytes(d.size || 0)} · uploaded by {d.uploaded_by || "unknown"} · {new Date(d.created_at).toLocaleDateString("en-GB")}</p>
                    </div>
                  </div>
                  <a href={`${process.env.REACT_APP_BACKEND_URL || ''}/api/referrer-documents/${caseId}/${d.id}/download?token=${encodeURIComponent(token)}`}
                    target="_blank" rel="noopener noreferrer" className="text-xs underline ml-3 shrink-0" style={{ color: "#100f5d" }}>
                    Download
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Attach a file already on this case to a message */}
      <Dialog open={docPickerOpen} onOpenChange={setDocPickerOpen}>
        <DialogContent className="max-w-lg" data-testid="doc-picker-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Attach from case documents</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Search documents…"
            value={docPickerSearch}
            onChange={(e) => setDocPickerSearch(e.target.value)}
            className="mb-2"
            data-testid="doc-picker-search"
          />
          <div className="max-h-[380px] overflow-y-auto -mx-1 px-1 space-y-1">
            {(() => {
              const q = docPickerSearch.trim().toLowerCase();
              const list = caseDocs.filter((d) => !q || (d.original_filename || "").toLowerCase().includes(q));
              if (list.length === 0) {
                return <p className="text-sm text-slate-400 py-8 text-center" data-testid="doc-picker-empty">No documents on this case yet.</p>;
              }
              return list.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => attachFromReferrerDoc(d)}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 text-left transition-colors"
                  data-testid={`doc-picker-item-${d.id}`}
                >
                  <FileText weight="bold" className="w-4 h-4 text-slate-400 shrink-0" />
                  <span className="flex-1 min-w-0 truncate text-sm text-slate-700">{d.original_filename}</span>
                  <span className="text-xs shrink-0" style={{ color: "#100f5d" }}>Attach</span>
                </button>
              ));
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocPickerOpen(false)} data-testid="doc-picker-done-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
